import React, { useState, useEffect } from 'react';
import {
  Button,
  Card,
  Flex,
  Heading,
  Text,
  View,
  Badge,
  Divider,
  Alert,
  Loader,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  SelectField,
  CheckboxField
} from '@aws-amplify/ui-react';
import corsProxy from '../../utils/corsProxy';
import { getStatusBadgeVariation, formatDate } from '../../utils/helpers';
import ConfirmDialog from '../common/ConfirmDialog';

const ProjectDetails = ({ project, user, onClose, onEdit, onManageParticipants }) => {
  const [participants, setParticipants] = useState([]);
  const [loadingParticipants, setLoadingParticipants] = useState(true);
  const [participantError, setParticipantError] = useState(null);
  
  // Admin management state
  const [admins, setAdmins] = useState([]);
  const [loadingAdmins, setLoadingAdmins] = useState(true);
  const [adminError, setAdminError] = useState(null);
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [addingAdmin, setAddingAdmin] = useState(false);
  
  // Admin functions state
  const [projectStatus, setProjectStatus] = useState(project?.status || 'Setup');
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [selectedParticipants, setSelectedParticipants] = useState(new Set());
  const [showDeleteOptions, setShowDeleteOptions] = useState(false);
  const [showResortOptions, setShowResortOptions] = useState(false);
  const [selectedForReorder, setSelectedForReorder] = useState(new Set());
  const [randomizingOrder, setRandomizingOrder] = useState(false);
  
  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    onConfirm: null,
    variant: 'destructive'
  });

  useEffect(() => {
    if (project && project.projectId) {
      fetchParticipants();
    }
  }, [project]);

  // Fetch admins after participants are loaded
  useEffect(() => {
    if (project && project.projectId && !loadingParticipants) {
      fetchAdmins();
    }
  }, [project, loadingParticipants]);

  const fetchParticipants = async () => {
    try {
      setLoadingParticipants(true);
      setParticipantError(null);
      
      console.log('Fetching participants for project details:', project.projectId);
      
      // Use the individual project endpoint to get complete project data including all participants
      const response = await corsProxy.get(`/projects/${project.projectId}`);
      
      console.log('ProjectDetails API response:', response);
      console.log('ProjectDetails response.data:', response.data);
      
      // Handle corsProxy response format and extract participant details
      let projectData = {};
      if (response.data) {
        projectData = response.data;
      } else {
        projectData = response;
      }
      
      // Extract participants from the project's participantDetails array
      let participantsData = [];
      if (projectData.participantDetails && Array.isArray(projectData.participantDetails)) {
        participantsData = projectData.participantDetails.map(participant => ({
          id: participant.email, // Use email as ID
          email: participant.email,
          name: participant.name,
          order: participant.order,
          status: participant.status,
          // Use actual selected number from database (null if not selected)
          selectedNumber: participant.selectedNumber === null || participant.selectedNumber === undefined ? null : participant.selectedNumber
        }));
      }
      
      console.log('ProjectDetails setting participants from project data (real selection numbers):', participantsData);
      
      setParticipants(participantsData);
      console.log('Loaded participants for project details:', participantsData);
      
    } catch (error) {
      console.error('Error fetching participants for project details:', error);
      setParticipantError('Failed to load participants');
      setParticipants([]);
    } finally {
      setLoadingParticipants(false);
    }
  };

  // Function to determine current selection state
  const getSelectionState = () => {
    if (!participants.length) return { activeSelectors: [], nextUp: null };

    // Don't show anyone as actively selecting if project is not Active
    if (project.status !== 'Active') {
      return { activeSelectors: [], nextUp: null, notStarted: true };
    }

    // Sort participants by selection order
    const sortedParticipants = [...participants].sort((a, b) => (a.order || 0) - (b.order || 0));

    // Find participants who haven't selected yet and aren't skipped
    const activeParticipants = sortedParticipants.filter(p => !p.selectedNumber && p.status !== 'Skipped');

    if (activeParticipants.length === 0) {
      return { activeSelectors: [], nextUp: null, allComplete: true };
    }

    // Current turn: first active unselected participant (matches backend enforcement)
    const currentTurn = activeParticipants[0];
    const nextUp = activeParticipants.length > 1 ? activeParticipants[1] : null;

    return {
      activeSelectors: [currentTurn],
      nextUp,
      allComplete: false
    };
  };

  // Function to get participant display status
  const getParticipantSelectionStatus = (participant) => {
    const selectionState = getSelectionState();

    // If project is not active, show all as Pending
    if (selectionState.notStarted) {
      return {
        status: 'pending',
        badge: 'default',
        text: 'Pending'
      };
    }

    if (participant.selectedNumber) {
      return {
        status: 'completed',
        badge: 'success',
        text: `Selected #${participant.selectedNumber}`
      };
    }

    if (selectionState.activeSelectors.some(p => p.id === participant.id)) {
      return {
        status: 'active',
        badge: 'warning',
        text: 'Currently Selecting'
      };
    }

    if (selectionState.nextUp && selectionState.nextUp.id === participant.id) {
      return {
        status: 'next',
        badge: 'info',
        text: 'Next Up'
      };
    }

    return {
      status: 'waiting',
      badge: 'default',
      text: 'Waiting'
    };
  };

  const fetchAdmins = async () => {
    try {
      setLoadingAdmins(true);
      setAdminError(null);
      
      console.log('Fetching admins for project:', project.projectId);
      
      // For now, we'll identify admins based on the project's adminId and any additional admin logic
      // In a real implementation, this would call an API to get project admins
      
      const currentUserEmail = user?.attributes?.email || user?.username || '';
      const currentUserName = user?.attributes?.name || currentUserEmail.split('@')[0];
      
      // Check if current user is also a participant
      const isCurrentUserParticipant = participants.some(p => 
        p.email.toLowerCase() === currentUserEmail.toLowerCase()
      );
      
      // Create admin list - for now just the current user as owner
      const adminUsers = [{
        id: 'current-user',
        email: currentUserEmail,
        name: currentUserName,
        role: 'OWNER',
        isParticipant: isCurrentUserParticipant
      }];
      
      setAdmins(adminUsers);
      console.log('Loaded admins for project details:', adminUsers);
      
    } catch (error) {
      console.error('Error fetching admins for project details:', error);
      setAdminError('Failed to load admins');
      
      // Fallback: Add current user as owner
      const fallbackEmail = user?.attributes?.email || user?.username || '';
      const fallbackName = user?.attributes?.name || fallbackEmail.split('@')[0];
      const isCurrentUserParticipant = participants.some(p =>
        p.email.toLowerCase() === fallbackEmail.toLowerCase()
      );

      setAdmins([{
        id: 'current-user',
        email: fallbackEmail,
        name: fallbackName,
        role: 'OWNER',
        isParticipant: isCurrentUserParticipant
      }]);
    } finally {
      setLoadingAdmins(false);
    }
  };

  const handleAddAdmin = async () => {
    if (!newAdminEmail.trim()) {
      setAdminError('Please enter an email address');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newAdminEmail)) {
      setAdminError('Please enter a valid email address');
      return;
    }

    // Check if admin already exists
    if (admins.some(admin => admin.email.toLowerCase() === newAdminEmail.toLowerCase())) {
      setAdminError('This user is already an admin');
      return;
    }

    try {
      setAddingAdmin(true);
      setAdminError(null);
      
      console.log('Adding admin:', newAdminEmail);
      
      // Check if the email belongs to an existing participant
      const existingParticipant = participants.find(p => p.email.toLowerCase() === newAdminEmail.toLowerCase());
      const isParticipant = !!existingParticipant;
      
      // Create admin user in Cognito with admin role
      const adminData = {
        email: newAdminEmail,
        name: existingParticipant?.name || newAdminEmail.split('@')[0],
        role: 'ADMIN',
        projectId: project.projectId
      };
      
      console.log('Creating admin user with data:', adminData);
      
      // Call the same user creation API that's used for participants
      // but with admin role instead of participant role
      try {
        const response = await corsProxy.post('/users', adminData);
        console.log('Admin user created:', response);
        
        // Add the new admin to local state
        const newAdmin = {
          id: response.id || `admin-${Date.now()}`,
          email: newAdminEmail,
          name: adminData.name,
          role: 'ADMIN',
          isParticipant: isParticipant
        };
        
        setAdmins(prev => [...prev, newAdmin]);
        setNewAdminEmail('');
        
        console.log('Admin added successfully:', newAdmin);
        
      } catch (apiError) {
        console.error('API error creating admin:', apiError);
        
        // If API fails, still add to local state for testing
        const newAdmin = {
          id: `admin-${Date.now()}`,
          email: newAdminEmail,
          name: adminData.name,
          role: 'ADMIN',
          isParticipant: isParticipant
        };
        
        setAdmins(prev => [...prev, newAdmin]);
        setNewAdminEmail('');
        
        console.log('Admin added locally (API failed):', newAdmin);
      }
      
    } catch (error) {
      console.error('Error adding admin:', error);
      setAdminError('Failed to add admin: ' + error.message);
    } finally {
      setAddingAdmin(false);
    }
  };

  const handleRemoveAdmin = async (adminId) => {
    const adminToRemove = admins.find(admin => admin.id === adminId);
    if (!adminToRemove) {
      setAdminError('Admin not found');
      return;
    }

    setConfirmDialog({
      isOpen: true,
      title: 'Remove Admin',
      message: `Are you sure you want to remove ${adminToRemove.name || adminToRemove.email} as an admin? This action cannot be undone.`,
      confirmText: 'Remove Admin',
      onConfirm: async () => {
        try {
          console.log('Removing admin:', adminId);
          
          // In a real implementation, this would call an API to remove admin privileges
          // This might involve updating the user's custom:role attribute in Cognito
          // or removing them from the project's admin list
          
          try {
            // Call API to remove admin privileges
            const response = await corsProxy.delete(`/users/${adminId}/admin-role`);
            console.log('Admin privileges removed:', response);
          } catch (apiError) {
            console.error('API error removing admin:', apiError);
            // Continue with local removal even if API fails
          }
          
          // Remove from local state
          setAdmins(prev => prev.filter(admin => admin.id !== adminId));
          
          console.log('Admin removed successfully');
          
        } catch (error) {
          console.error('Error removing admin:', error);
          setAdminError('Failed to remove admin: ' + error.message);
        }
      },
      variant: 'destructive'
    });
  };

  // Admin function handlers
  const handleStatusChange = async (newStatus) => {
    try {
      setUpdatingStatus(true);
      console.log('Updating project status to:', newStatus);
      
      const previousStatus = projectStatus;
      
      // In a real implementation, this would call an API to update project status
      const response = await corsProxy.put(`/projects/${project.projectId}`, {
        status: newStatus
      });
      
      setProjectStatus(newStatus);
      console.log('Project status updated successfully');
      
      // If changing from non-Active to Active, send welcome emails
      if (newStatus === 'Active' && previousStatus !== 'Active') {
        console.log('Project activated - sending welcome emails to participants');
        await sendWelcomeEmails();
      }
      
    } catch (error) {
      console.error('Error updating project status:', error);
      alert(`Failed to update project status: ${error.message || 'Unknown error'}`);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const sendWelcomeEmails = async () => {
    try {
      console.log('Sending welcome emails to all participants');
      
      // Call API to send welcome emails to all project participants
      const response = await corsProxy.post(`/projects/${project.projectId}/send-welcome-emails`, {
        projectName: project.name,
        selectionTimeWindow: project.selectionTimeWindow,
        numberRange: project.numberRange
      });
      
      console.log('Welcome emails sent successfully:', response);
      
      // Show success message to admin
      // You could add a success state here if needed
      
    } catch (error) {
      console.error('Error sending welcome emails:', error);
      // Could add error handling UI here
    }
  };

  const handleSelectParticipant = (participantId, isSelected) => {
    console.log('Selecting participant:', participantId, 'isSelected:', isSelected);
    setSelectedParticipants(prev => {
      const newSet = new Set(prev);
      if (isSelected) {
        newSet.add(participantId);
      } else {
        newSet.delete(participantId);
      }
      console.log('Updated selected participants:', Array.from(newSet));
      return newSet;
    });
  };

  const handleSelectAllParticipants = (isSelected) => {
    console.log('Select all participants:', isSelected);
    if (isSelected) {
      // Select all participants (not just those with numbers)
      const allIds = participants.map(p => p.id || p.username || p.email);
      console.log('Selecting all participants:', allIds);
      setSelectedParticipants(new Set(allIds));
    } else {
      console.log('Deselecting all participants');
      setSelectedParticipants(new Set());
    }
  };

  // Reorder selection handlers
  const handleSelectParticipantForReorder = (participantId, isSelected) => {
    console.log('Selecting participant for reorder:', participantId, 'isSelected:', isSelected);
    setSelectedForReorder(prev => {
      const newSet = new Set(prev);
      if (isSelected) {
        newSet.add(participantId);
      } else {
        newSet.delete(participantId);
      }
      console.log('Updated selected for reorder:', Array.from(newSet));
      return newSet;
    });
  };

  const handleSelectAllParticipantsForReorder = (isSelected) => {
    console.log('Select all participants for reorder:', isSelected);
    if (isSelected) {
      const allIds = participants.map(p => p.id || p.username || p.email);
      console.log('Selecting all participants for reorder:', allIds);
      setSelectedForReorder(new Set(allIds));
    } else {
      console.log('Deselecting all participants for reorder');
      setSelectedForReorder(new Set());
    }
  };

  const handleDeleteSelectedNumbers = async () => {
    const selectedCount = selectedParticipants.size;
    if (selectedCount === 0) return;

    setConfirmDialog({
      isOpen: true,
      title: 'Delete Selected Numbers',
      message: `Are you sure you want to delete the selected numbers for ${selectedCount} participant${selectedCount > 1 ? 's' : ''}? This will remove their number selections but keep them in the project.`,
      confirmText: 'Delete Numbers',
      onConfirm: async () => {
        try {
          console.log('Deleting selected numbers for participants:', Array.from(selectedParticipants));
          
          // Update participants to remove selected numbers
          const updatedParticipants = participants.map(p => {
            const participantId = p.id || p.username || p.email;
            if (selectedParticipants.has(participantId) && p.selectedNumber) {
              console.log(`Removing selected number ${p.selectedNumber} from participant ${participantId}`);
              return { ...p, selectedNumber: null };
            }
            return p;
          });
          
          // Call API to persist the changes to the backend
          try {
            const response = await corsProxy.put(`/projects/${project.projectId}/participants`, {
              participants: updatedParticipants.map(p => ({
                email: p.email,
                name: p.name,
                order: p.order,
                status: p.status || 'Pending',
                selectedNumber: p.selectedNumber
              }))
            });
            console.log('Selected numbers deleted successfully in backend:', response);
          } catch (apiError) {
            console.error('API error deleting selected numbers:', apiError);
            alert(`Operation failed: ${apiError.message || 'Unknown error'}`);
          }
          
          // Update local state
          setParticipants(updatedParticipants);
          setSelectedParticipants(new Set());
          setShowDeleteOptions(false);
          
          console.log('Selected numbers deleted successfully');
          
        } catch (error) {
          console.error('Error deleting selected numbers:', error);
        }
      },
      variant: 'destructive'
    });
  };

  const handleDeleteSelectedParticipants = async () => {
    const selectedCount = selectedParticipants.size;
    if (selectedCount === 0) return;

    setConfirmDialog({
      isOpen: true,
      title: 'Delete Selected Participants',
      message: `Are you sure you want to permanently remove ${selectedCount} participant${selectedCount > 1 ? 's' : ''} from this project? This action cannot be undone and will remove all their data including selected numbers.`,
      confirmText: 'Delete Participants',
      onConfirm: async () => {
        try {
          console.log('Deleting selected participants:', Array.from(selectedParticipants));
          
          // Filter out selected participants
          const remainingParticipants = participants.filter(p => {
            const participantId = p.id || p.username || p.email;
            return !selectedParticipants.has(participantId);
          });
          
          console.log('Remaining participants after deletion:', remainingParticipants.length);
          
          // Call API to update participants list
          const response = await corsProxy.put(`/projects/${project.projectId}/participants`, {
            participants: remainingParticipants.map(p => ({
              email: p.email,
              name: p.name,
              order: p.order,
              status: p.status || 'Pending',
              selectedNumber: p.selectedNumber
            }))
          });
          
          console.log('Participants updated successfully:', response);
          
          // Update local state
          setParticipants(remainingParticipants);
          setSelectedParticipants(new Set());
          setShowDeleteOptions(false);
          
          console.log('Selected participants deleted successfully');
          
        } catch (error) {
          console.error('Error deleting selected participants:', error);
          alert(`Failed to delete participants: ${error.message || 'Unknown error'}`);
          const remainingParticipants = participants.filter(p => {
            const participantId = p.id || p.username || p.email;
            return !selectedParticipants.has(participantId);
          });
          setParticipants(remainingParticipants);
          setSelectedParticipants(new Set());
          setShowDeleteOptions(false);
        }
      },
      variant: 'destructive'
    });
  };

  const handleDeleteAllNumbers = async () => {
    const participantsWithNumbers = participants.filter(p => p.selectedNumber);
    if (participantsWithNumbers.length === 0) {
      console.log('No participants have selected numbers to delete');
      return;
    }

    setConfirmDialog({
      isOpen: true,
      title: 'Delete All Numbers',
      message: `Are you sure you want to delete all selected numbers from ${participantsWithNumbers.length} participant${participantsWithNumbers.length > 1 ? 's' : ''}? This will reset all number selections but keep all participants in the project.`,
      confirmText: 'Delete All Numbers',
      onConfirm: async () => {
        try {
          console.log('Deleting all selected numbers');
          
          // Update all participants to remove selected numbers
          const updatedParticipants = participants.map(p => ({
            ...p,
            selectedNumber: null
          }));
          
          // Call API to persist the changes to the backend
          try {
            const response = await corsProxy.put(`/projects/${project.projectId}/participants`, {
              participants: updatedParticipants.map(p => ({
                email: p.email,
                name: p.name,
                order: p.order,
                status: p.status || 'Pending',
                selectedNumber: null // Explicitly set to null
              }))
            });
            console.log('All numbers deleted successfully in backend:', response);
          } catch (apiError) {
            console.error('API error deleting all numbers:', apiError);
            alert(`Operation failed: ${apiError.message || 'Unknown error'}`);
          }
          
          // Update local state
          setParticipants(updatedParticipants);
          setShowDeleteOptions(false);
          
          console.log('All selected numbers deleted successfully');
          
        } catch (error) {
          console.error('Error deleting all numbers:', error);
        }
      },
      variant: 'destructive'
    });
  };

  const handleRandomizeOrder = async () => {
    setConfirmDialog({
      isOpen: true,
      title: 'Randomize Selection Order',
      message: `Are you sure you want to randomize the selection order for all ${participants.length} participants? This will randomly assign new selection order numbers to everyone.`,
      confirmText: 'Randomize Order',
      onConfirm: async () => {
        try {
          setRandomizingOrder(true);
          console.log('Randomizing participant selection order');
          
          // Create a copy of participants and shuffle their order
          let updatedParticipants = [...participants];
          for (let i = updatedParticipants.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [updatedParticipants[i], updatedParticipants[j]] = [updatedParticipants[j], updatedParticipants[i]];
          }
          
          // Assign new order numbers
          updatedParticipants = updatedParticipants.map((p, index) => ({
            ...p,
            order: index + 1
          }));
          
          // Call API to persist the changes to the backend
          try {
            const response = await corsProxy.put(`/projects/${project.projectId}/participants`, {
              participants: updatedParticipants.map(p => ({
                email: p.email,
                name: p.name,
                order: p.order,
                status: p.status || 'Pending',
                selectedNumber: p.selectedNumber
              }))
            });
            console.log('Selection order randomized successfully in backend:', response);
          } catch (apiError) {
            console.error('API error randomizing selection order:', apiError);
            alert(`Operation failed: ${apiError.message || 'Unknown error'}`);
          }
          
          // Update local state
          setParticipants(updatedParticipants);
          console.log('Participant order randomized successfully');
          
        } catch (error) {
          console.error('Error randomizing order:', error);
        } finally {
          setRandomizingOrder(false);
        }
      },
      variant: 'primary'
    });
  };

  const handleRandomizeSelectedOrder = async () => {
    if (selectedForReorder.size === 0) {
      console.log('No participants selected for reordering');
      return;
    }

    setConfirmDialog({
      isOpen: true,
      title: 'Randomize Selected Order',
      message: `Are you sure you want to randomize the selection order for ${selectedForReorder.size} selected participant${selectedForReorder.size > 1 ? 's' : ''}? This will shuffle their positions while keeping other participants in their current order.`,
      confirmText: 'Randomize Selected',
      onConfirm: async () => {
        try {
          setRandomizingOrder(true);
          console.log('Randomizing selected participants order:', Array.from(selectedForReorder));
          
          // Get selected participants and their current orders
          const selectedParticipants = participants.filter(p => {
            const participantId = p.id || p.username || p.email;
            return selectedForReorder.has(participantId);
          });
          
          // Get their current order numbers
          const orderNumbers = selectedParticipants.map(p => p.order).sort((a, b) => a - b);
          
          // Shuffle the selected participants
          const shuffledParticipants = [...selectedParticipants];
          for (let i = shuffledParticipants.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffledParticipants[i], shuffledParticipants[j]] = [shuffledParticipants[j], shuffledParticipants[i]];
          }
          
          // Assign the original order numbers to the shuffled participants
          shuffledParticipants.forEach((participant, index) => {
            participant.order = orderNumbers[index];
          });
          
          // Update the participants array
          const updatedParticipants = participants.map(p => {
            const participantId = p.id || p.username || p.email;
            const shuffledParticipant = shuffledParticipants.find(sp => {
              const spId = sp.id || sp.username || sp.email;
              return spId === participantId;
            });
            return shuffledParticipant || p;
          });
          
          // Call API to persist the changes to the backend
          try {
            const response = await corsProxy.put(`/projects/${project.projectId}/participants`, {
              participants: updatedParticipants.map(p => ({
                email: p.email,
                name: p.name,
                order: p.order,
                status: p.status || 'Pending',
                selectedNumber: p.selectedNumber
              }))
            });
            console.log('Selected participants order randomized successfully in backend:', response);
          } catch (apiError) {
            console.error('API error randomizing selected order:', apiError);
            alert(`Operation failed: ${apiError.message || 'Unknown error'}`);
          }
          
          // Update local state
          setParticipants(updatedParticipants);
          setSelectedForReorder(new Set());
          setShowResortOptions(false);
          
          console.log('Selected participants order randomized successfully');
          
        } catch (error) {
          console.error('Error randomizing selected order:', error);
        } finally {
          setRandomizingOrder(false);
        }
      },
      variant: 'primary'
    });
  };

  const handleRandomizeAllOrder = async () => {
    setConfirmDialog({
      isOpen: true,
      title: 'Randomize All Selection Order',
      message: `Are you sure you want to randomize the selection order for all ${participants.length} participants? This will completely shuffle everyone's position in the selection queue.`,
      confirmText: 'Randomize All',
      onConfirm: async () => {
        try {
          setRandomizingOrder(true);
          console.log('Randomizing all participants order');
          
          // Create a shuffled array of participants with new random order
          const shuffledParticipants = [...participants];
          
          // Fisher-Yates shuffle algorithm
          for (let i = shuffledParticipants.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffledParticipants[i], shuffledParticipants[j]] = [shuffledParticipants[j], shuffledParticipants[i]];
          }
          
          // Assign new order numbers
          const reorderedParticipants = shuffledParticipants.map((participant, index) => ({
            ...participant,
            order: index + 1
          }));
          
          // Call API to persist the changes to the backend
          try {
            const response = await corsProxy.put(`/projects/${project.projectId}/participants`, {
              participants: reorderedParticipants.map(p => ({
                email: p.email,
                name: p.name,
                order: p.order,
                status: p.status || 'Pending',
                selectedNumber: p.selectedNumber
              }))
            });
            console.log('All participants order randomized successfully in backend:', response);
          } catch (apiError) {
            console.error('API error randomizing all order:', apiError);
            alert(`Operation failed: ${apiError.message || 'Unknown error'}`);
          }
          
          // Update local state
          setParticipants(reorderedParticipants);
          setShowResortOptions(false);
          
          console.log('All participants order randomized successfully');
          
        } catch (error) {
          console.error('Error randomizing all order:', error);
        } finally {
          setRandomizingOrder(false);
        }
      },
      variant: 'primary'
    });
  };
  if (!project) {
    return (
      <Alert variation="error">
        Project not found
      </Alert>
    );
  }

  const getStatusBadge = (status) => {
    return <Badge variation={getStatusBadgeVariation(status)}>{status}</Badge>;
  };

  return (
    <Card variation="elevated" padding="1.5rem">
      
      <Flex justifyContent="space-between" alignItems="center">
        <Heading level={3}>Project Details</Heading>
        <Button variation="link" onClick={onClose}>
          Back to Projects
        </Button>
      </Flex>
      
      <Divider marginTop="1rem" marginBottom="1rem" />
      
      <Flex direction="column" gap="1rem">
        <Flex justifyContent="space-between" alignItems="center">
          <Heading level={4}>{project.name}</Heading>
          <Flex gap="1rem" alignItems="center">
            <SelectField
              label="Project Status"
              value={projectStatus}
              onChange={(e) => handleStatusChange(e.target.value)}
              isDisabled={updatingStatus}
              size="small"
            >
              <option value="Setup">Setup</option>
              <option value="Active">Active</option>
              <option value="Paused">Paused</option>
              <option value="Archived">Archived</option>
            </SelectField>
            {getStatusBadge(projectStatus)}
          </Flex>
        </Flex>
        
        <Text>
          {project.description || 'No description provided'}
        </Text>
        
        <Flex gap="1rem" wrap="wrap">
          <Card variation="outlined" padding="1rem" width="fit-content">
            <Text fontWeight="bold">Number Range</Text>
            <Text>{(project.numberRange?.min || project.minNumber)} - {(project.numberRange?.max || project.maxNumber)}</Text>
          </Card>
          
          <Card variation="outlined" padding="1rem" width="fit-content">
            <Text fontWeight="bold">Participants</Text>
            <Text>{project.participants || 0}</Text>
          </Card>
          
          <Card variation="outlined" padding="1rem" width="fit-content">
            <Text fontWeight="bold">Selection Time Window</Text>
            <Text>{project.selectionTimeWindow} hours</Text>
          </Card>
          
          <Card variation="outlined" padding="1rem" width="fit-content">
            <Text fontWeight="bold">Current Round</Text>
            <Text>{project.currentRound || 0}</Text>
          </Card>
        </Flex>
        
        <Flex gap="1rem" wrap="wrap">
          <Card variation="outlined" padding="1rem" width="fit-content">
            <Text fontWeight="bold">Created</Text>
            <Text>{formatDate(project.createdAt)}</Text>
          </Card>
          
          <Card variation="outlined" padding="1rem" width="fit-content">
            <Text fontWeight="bold">Last Updated</Text>
            <Text>{formatDate(project.updatedAt)}</Text>
          </Card>
        </Flex>
        
        <Divider marginTop="1rem" marginBottom="1rem" />
        
        <Card variation="outlined" padding="1rem">
          <Flex justifyContent="space-between" alignItems="center">
            <Heading level={5}>Participant List</Heading>
            <Button size="small" onClick={onManageParticipants}>
              Manage Participants
            </Button>
          </Flex>
          {(() => {
            console.log('ProjectDetails render - loadingParticipants:', loadingParticipants);
            console.log('ProjectDetails render - participantError:', participantError);
            console.log('ProjectDetails render - participants:', participants);
            console.log('ProjectDetails render - participants.length:', participants.length);
            return null;
          })()}
          
          {/* Selection Status Summary */}
          {participants.length > 0 && (
            <Card variation="outlined" padding="1rem" marginTop="1rem" backgroundColor="var(--amplify-colors-background-secondary)">
              <Heading level={6}>Current Selection Status</Heading>
              {(() => {
                const selectionState = getSelectionState();
                if (selectionState.allComplete) {
                  return (
                    <Text color="green">
                      🎉 All participants have completed their selections!
                    </Text>
                  );
                }
                if (selectionState.activeSelectors.length > 0) {
                  return (
                    <Flex direction="column" gap="0.5rem">
                      <Text>
                        <strong>Currently selecting:</strong> {selectionState.activeSelectors.map(p => p.name || p.email).join(', ')}
                      </Text>
                      {selectionState.nextUp && (
                        <Text>
                          <strong>Next up:</strong> {selectionState.nextUp.name || selectionState.nextUp.email}
                        </Text>
                      )}
                    </Flex>
                  );
                }
                return <Text>Waiting for selection process to begin...</Text>;
              })()}
            </Card>
          )}
          
          {loadingParticipants ? (
            <Flex justifyContent="center" marginTop="1rem">
              <Loader />
              <Text marginLeft="0.5rem">Loading participants...</Text>
            </Flex>
          ) : participantError ? (
            <Alert variation="error" marginTop="1rem">
              {participantError}
            </Alert>
          ) : participants.length > 0 ? (
            <View as="table" width="100%" marginTop="1rem" style={{ borderCollapse: 'collapse' }}>
              <View as="thead">
                <View as="tr" style={{ borderBottom: '1px solid #ccc' }}>
                  {showDeleteOptions && (
                    <View as="th" padding="0.5rem" textAlign="left">
                      <CheckboxField
                        label=""
                        checked={selectedParticipants.size === participants.length && participants.length > 0}
                        onChange={(e) => handleSelectAllParticipants(e.target.checked)}
                        size="small"
                      />
                    </View>
                  )}
                  {showResortOptions && (
                    <View as="th" padding="0.5rem" textAlign="left">
                      <CheckboxField
                        label=""
                        checked={selectedForReorder.size === participants.length && participants.length > 0}
                        onChange={(e) => handleSelectAllParticipantsForReorder(e.target.checked)}
                        size="small"
                      />
                    </View>
                  )}
                  <View as="th" padding="0.5rem" textAlign="left">Selection Order</View>
                  <View as="th" padding="0.5rem" textAlign="left">Name</View>
                  <View as="th" padding="0.5rem" textAlign="left">Email</View>
                  <View as="th" padding="0.5rem" textAlign="left">Number Selected</View>
                  <View as="th" padding="0.5rem" textAlign="left">Selection Status</View>
                  <View as="th" padding="0.5rem" textAlign="left">Account Status</View>
                </View>
              </View>
              <View as="tbody">
                {participants
                  .sort((a, b) => (a.order || 0) - (b.order || 0)) // Sort by selection order
                  .map((participant, index) => {
                    const selectionStatus = getParticipantSelectionStatus(participant);
                    const participantId = participant.id || participant.username || participant.email;
                    const hasSelectedNumber = participant.selectedNumber !== null && participant.selectedNumber !== undefined;
                    
                    console.log('Rendering participant:', {
                      id: participantId,
                      email: participant.email,
                      selectedNumber: participant.selectedNumber,
                      hasSelectedNumber
                    });
                    
                    return (
                      <View as="tr" key={participantId || index} style={{ borderBottom: '1px solid #eee' }}>
                        {showDeleteOptions && (
                          <View as="td" padding="0.5rem">
                            <CheckboxField
                              label=""
                              checked={selectedParticipants.has(participantId)}
                              onChange={(e) => handleSelectParticipant(participantId, e.target.checked)}
                              size="small"
                            />
                          </View>
                        )}
                        {showResortOptions && (
                          <View as="td" padding="0.5rem">
                            <CheckboxField
                              label=""
                              checked={selectedForReorder.has(participantId)}
                              onChange={(e) => handleSelectParticipantForReorder(participantId, e.target.checked)}
                              size="small"
                            />
                          </View>
                        )}
                        <View as="td" padding="0.5rem">
                          <Text fontWeight="bold">#{participant.order || index + 1}</Text>
                        </View>
                        <View as="td" padding="0.5rem">{participant.name || 'Unknown'}</View>
                        <View as="td" padding="0.5rem">{participant.email}</View>
                        <View as="td" padding="0.5rem">
                          {hasSelectedNumber ? (
                            <Badge variation="success">#{participant.selectedNumber}</Badge>
                          ) : (
                            <Text color="gray">Not selected</Text>
                          )}
                        </View>
                        <View as="td" padding="0.5rem">
                          <Badge variation={selectionStatus.badge}>
                            {selectionStatus.text}
                          </Badge>
                        </View>
                        <View as="td" padding="0.5rem">
                          <Badge 
                            variation={participant.status === 'CONFIRMED' ? 'success' : 
                                      participant.status === 'FORCE_CHANGE_PASSWORD' ? 'warning' : 'info'}
                          >
                            {participant.status === 'FORCE_CHANGE_PASSWORD' ? 'Setup Required' : 
                             participant.status === 'CONFIRMED' ? 'Active' : participant.status}
                          </Badge>
                        </View>
                      </View>
                    );
                  })}
              </View>
            </View>
          ) : (
            <Text marginTop="1rem">No participants added yet.</Text>
          )}
        </Card>
        
        <Divider marginTop="1rem" marginBottom="1rem" />
        
        <Card variation="outlined" padding="1rem">
          <Heading level={5}>Admin Functions</Heading>
          
          <Flex direction="column" gap="1rem" marginTop="1rem">
            {/* Number Management */}
            <Card variation="outlined" padding="1rem">
              <Heading level={6}>Participant Management</Heading>
              <Flex gap="1rem" marginTop="1rem" wrap="wrap" alignItems="center">
                <Button
                  variation="destructive"
                  size="small"
                  onClick={() => setShowDeleteOptions(!showDeleteOptions)}
                >
                  {showDeleteOptions ? 'Cancel' : 'Delete Options'}
                </Button>
                
                {showDeleteOptions && (
                  <>
                    <Button
                      variation="destructive"
                      size="small"
                      onClick={handleDeleteAllNumbers}
                    >
                      Delete All Numbers
                    </Button>
                    
                    <Button
                      variation="destructive"
                      size="small"
                      onClick={handleDeleteSelectedNumbers}
                      isDisabled={selectedParticipants.size === 0}
                    >
                      Delete Selected Numbers ({selectedParticipants.size})
                    </Button>
                    
                    <Button
                      variation="destructive"
                      size="small"
                      onClick={handleDeleteSelectedParticipants}
                      isDisabled={selectedParticipants.size === 0}
                    >
                      Delete Selected Participants ({selectedParticipants.size})
                    </Button>
                  </>
                )}
              </Flex>
              
              {showDeleteOptions && (
                <Text fontSize="0.9rem" color="gray" marginTop="0.5rem">
                  Select participants in the table below, then choose to delete their numbers only or remove them from the project entirely.
                </Text>
              )}
            </Card>
            
            {/* Selection Order Management */}
            <Card variation="outlined" padding="1rem">
              <Heading level={6}>Selection Order</Heading>
              <Flex gap="1rem" marginTop="1rem" wrap="wrap" alignItems="center">
                <Button
                  onClick={() => setShowResortOptions(!showResortOptions)}
                  size="small"
                >
                  {showResortOptions ? 'Cancel' : 'Randomize Order'}
                </Button>
                
                {showResortOptions && (
                  <>
                    <Button
                      onClick={handleRandomizeAllOrder}
                      isLoading={randomizingOrder}
                      loadingText="Randomizing..."
                      size="small"
                      variation="primary"
                    >
                      Randomize All
                    </Button>
                    
                    <Button
                      onClick={handleRandomizeSelectedOrder}
                      isLoading={randomizingOrder}
                      loadingText="Randomizing..."
                      size="small"
                      isDisabled={selectedForReorder.size === 0}
                    >
                      Randomize Selected ({selectedForReorder.size})
                    </Button>
                  </>
                )}
              </Flex>
              
              {showResortOptions && (
                <Text fontSize="0.9rem" color="gray" marginTop="0.5rem">
                  Select participants in the table below to randomize specific orders, or randomize all participants at once.
                </Text>
              )}
            </Card>
          </Flex>
        </Card>
        
        <Card variation="outlined" padding="1rem">
          <Heading level={5}>Project Admins</Heading>
          {loadingAdmins ? (
            <Flex justifyContent="center" marginTop="1rem">
              <Loader />
              <Text marginLeft="0.5rem">Loading admins...</Text>
            </Flex>
          ) : adminError ? (
            <Alert variation="error" marginTop="1rem">
              {adminError}
            </Alert>
          ) : (
            <>
              {admins.length > 0 ? (
                <Table highlightOnHover={true} marginTop="1rem">
                  <TableHead>
                    <TableRow>
                      <TableCell as="th">Name</TableCell>
                      <TableCell as="th">Email</TableCell>
                      <TableCell as="th">Role</TableCell>
                      <TableCell as="th">Also Participant</TableCell>
                      <TableCell as="th">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {admins.map((admin) => (
                      <TableRow key={admin.id}>
                        <TableCell>{admin.name}</TableCell>
                        <TableCell>{admin.email}</TableCell>
                        <TableCell>
                          <Badge variation={admin.role === 'Owner' ? 'success' : 'info'}>
                            {admin.role}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variation={admin.isParticipant ? 'warning' : 'default'}>
                            {admin.isParticipant ? 'Yes' : 'No'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {admin.role !== 'Owner' && (
                            <Button 
                              size="small" 
                              variation="destructive" 
                              onClick={() => handleRemoveAdmin(admin.id)}
                            >
                              Remove
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Text marginTop="1rem">No admins found.</Text>
              )}
              
              <Divider marginTop="1rem" marginBottom="1rem" />
              
              <Heading level={6}>Add New Admin</Heading>
              <Flex gap="1rem" marginTop="1rem" alignItems="flex-end">
                <TextField
                  label="Email Address"
                  value={newAdminEmail}
                  onChange={(e) => setNewAdminEmail(e.target.value)}
                  placeholder="admin@example.com"
                  isDisabled={addingAdmin}
                />
                <Button 
                  onClick={handleAddAdmin}
                  isLoading={addingAdmin}
                  loadingText="Adding..."
                >
                  Add Admin
                </Button>
              </Flex>
              <Text fontSize="0.9rem" color="gray" marginTop="0.5rem">
                If the email belongs to an existing participant, they will be marked as both admin and participant.
              </Text>
            </>
          )}
        </Card>
        
        <Divider marginTop="1rem" marginBottom="1rem" />
        
        <Flex justifyContent="space-between">
          <Button onClick={() => onEdit(project)}>
            Edit Project
          </Button>
          
          <Button variation="primary" onClick={() => onManageParticipants(project)}>
            Manage Participants
          </Button>
        </Flex>
      </Flex>

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText}
        variant={confirmDialog.variant}
        onCancel={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        onConfirm={() => {
          setConfirmDialog({ ...confirmDialog, isOpen: false });
          if (confirmDialog.onConfirm) {
            confirmDialog.onConfirm();
          }
        }}
      />
    </Card>
  );
};

export default ProjectDetails;
